import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { Paperclip, X, FileText, ImageIcon, Eye, ExternalLink, PencilLine } from "lucide-react";
import { toast } from "react-toastify";
import { compressImage } from "@/Wist-blockchain/lib-blockchain/instrument-compress-images";
import { AuthImage } from "@/Wist-blockchain/instruments/components/AuthImage";
import { getReportFilePreviewUrl } from "../utils/uploadAnexosStorage";
import { client } from "@/lib/appwrite";

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB

export interface PendingFile {
    localId: string;
    file: File;
    previewUrl: string;
    name: string;
    type: string;
    description: string;
}

// ─── Auth fetch + open in new tab (for docs/PDFs) ────────────────────────────

const openAuthFile = async (url: string) => {
    try {
        const headers = (client as unknown as { headers: Record<string, string> }).headers ?? {}
        const res = await fetch(url, { headers })
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        window.open(blobUrl, "_blank")
        setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000)
    } catch {
        window.open(url, "_blank")
    }
}

// ─── Auth lightbox ────────────────────────────────────────────────────────────

function AuthLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
            onClick={onClose}
        >
            <div className="relative max-w-4xl w-full px-4" onClick={e => e.stopPropagation()}>
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute -top-10 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer"
                >
                    <X className="h-4 w-4" />
                </button>
                <AuthImage
                    src={src}
                    alt={alt}
                    className="max-h-[80vh] w-full rounded-xl object-contain"
                />
            </div>
        </div>
    )
}

// ─── Description popover (shared by both card types) ─────────────────────────

function DescriptionPopover({ value, onChange }: { value: string; onChange: (v: string) => void }) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="w-full flex items-center gap-1 px-1.5 py-1 rounded border border-border/40 bg-muted/20 hover:bg-muted/60 hover:border-border transition-colors cursor-pointer text-left min-h-6.5"
                >
                    <PencilLine className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className={`text-[10px] leading-tight truncate flex-1 ${value ? "text-foreground" : "text-muted-foreground"}`}>
                        {value || "Descripción..."}
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="start" className="w-64 p-3 space-y-2">
                <p className="text-xs font-medium text-foreground">Descripción del anexo</p>
                <Textarea
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    placeholder="Descripción..."
                    className="text-sm resize-none"
                    rows={4}
                    autoFocus
                />
            </PopoverContent>
        </Popover>
    )
}



function ExistingFileCard({
    fileId,
    description,
    onRemove,
    onDescriptionChange,
}: {
    fileId: string
    description: string
    onRemove: () => void
    onDescriptionChange: (desc: string) => void
}) {
    const [isDoc, setIsDoc] = useState(false)
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const url = getReportFilePreviewUrl(fileId)

    const handleClick = () => {
        if (isDoc) openAuthFile(url)
        else setLightboxOpen(true)
    }

    return (
        <>
            <div className="flex flex-col gap-1 w-28 shrink-0">
                {/* Fixed-size card */}
                <div
                    className="relative group rounded-lg border border-border/50 bg-muted/30 overflow-hidden h-24 cursor-pointer"
                    onClick={handleClick}
                >
                    <AuthImage
                        src={url}
                        alt={`Anexo ${fileId}`}
                        className="w-full h-full object-cover"
                        fallback={
                            <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-muted px-2">
                                <FileText className="w-7 h-7 text-muted-foreground shrink-0" />
                                <span className="text-[9px] text-muted-foreground text-center leading-tight line-clamp-2">
                                    Documento
                                </span>
                            </div>
                        }
                        onError={() => setIsDoc(true)}
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                        {isDoc
                            ? <ExternalLink className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            : <Eye className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        }
                    </div>
                    {/* Remove button */}
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onRemove() }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10"
                    >
                        <X className="w-3 h-3" />
                    </button>
                    {/* Saved badge */}
                    <span className="absolute bottom-1 left-1 text-[8px] bg-emerald-500 text-white rounded px-1 leading-tight">
                        guardado
                    </span>
                </div>
                <DescriptionPopover value={description} onChange={onDescriptionChange} />
            </div>

            {lightboxOpen && !isDoc && (
                <AuthLightbox
                    src={url}
                    alt={`Anexo ${fileId}`}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </>
    )
}

// ─── Pending file card (local, not yet uploaded) ─────────────────────────────

function PendingFileCard({
    file,
    onRemove,
    onDescriptionChange,
}: {
    file: PendingFile
    onRemove: () => void
    onDescriptionChange: (desc: string) => void
}) {
    const [lightboxOpen, setLightboxOpen] = useState(false)
    const isImage = file.type.startsWith("image/")

    const handleClick = () => {
        if (isImage) setLightboxOpen(true)
    }

    return (
        <>
            <div className="flex flex-col gap-1 w-28 shrink-0">
                {/* Fixed-size card */}
                <div
                    className={`relative group rounded-lg border border-border/50 bg-muted/30 overflow-hidden h-24 ${isImage ? "cursor-pointer" : ""}`}
                    onClick={handleClick}
                >
                    {isImage ? (
                        <img src={file.previewUrl} alt={file.name} className="w-full h-full object-cover" />
                    ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-1 bg-muted px-2">
                            <FileText className="w-7 h-7 text-muted-foreground shrink-0" />
                            <span className="text-[9px] text-muted-foreground text-center leading-tight line-clamp-2">
                                {file.name}
                            </span>
                        </div>
                    )}
                    {/* Hover overlay for images */}
                    {isImage && (
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                            <Eye className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    )}
                    {/* Remove button */}
                    <button
                        type="button"
                        onClick={e => { e.stopPropagation(); onRemove() }}
                        className="absolute top-1 right-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10"
                    >
                        <X className="w-3 h-3" />
                    </button>
                    {/* New badge */}
                    <span className="absolute bottom-1 left-1 text-[8px] bg-blue-500 text-white rounded px-1 leading-tight">
                        nuevo
                    </span>
                </div>
                <DescriptionPopover value={file.description} onChange={onDescriptionChange} />
            </div>

            {lightboxOpen && isImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
                    onClick={() => setLightboxOpen(false)}
                >
                    <div className="relative max-w-4xl w-full px-4" onClick={e => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => setLightboxOpen(false)}
                            className="absolute -top-10 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white hover:bg-white/30 cursor-pointer"
                        >
                            <X className="h-4 w-4" />
                        </button>
                        <img
                            src={file.previewUrl}
                            alt={file.name}
                            className="max-h-[80vh] w-full rounded-xl object-contain"
                        />
                    </div>
                </div>
            )}
        </>
    )
}

// ─── Main uploader ────────────────────────────────────────────────────────────

interface ReportFilesUploaderProps {
    files: PendingFile[];
    onChange: (files: PendingFile[]) => void;
    existingFileIds?: string[];
    existingDescriptions?: string[];
    onRemoveExisting?: (index: number) => void;
    onUpdateExistingDescription?: (index: number, desc: string) => void;
}

export function ReportFilesUploader({
    files,
    onChange,
    existingFileIds = [],
    existingDescriptions = [],
    onRemoveExisting,
    onUpdateExistingDescription,
}: ReportFilesUploaderProps) {
    const inputRef = useRef<HTMLInputElement>(null);
    const totalCount = existingFileIds.length + files.length

    const handleFiles = async (fileList: FileList) => {
        const allowed: File[] = []
        for (const file of Array.from(fileList)) {
            if (file.size > MAX_FILE_SIZE) {
                toast.error(`"${file.name}" supera el límite de 5 MB`)
            } else {
                allowed.push(file)
            }
        }
        if (!allowed.length) return

        const processed: PendingFile[] = await Promise.all(
            allowed.map(async (file) => {
                const finalFile = file.type.startsWith("image/")
                    ? await compressImage(file)
                    : file;
                return {
                    localId: crypto.randomUUID(),
                    file: finalFile,
                    previewUrl: URL.createObjectURL(finalFile),
                    name: finalFile.name,
                    type: finalFile.type,
                    description: "",
                };
            })
        );

        onChange([...files, ...processed]);
    };

    const handleRemovePending = (localId: string) => {
        const target = files.find((f) => f.localId === localId);
        if (target) URL.revokeObjectURL(target.previewUrl);
        onChange(files.filter((f) => f.localId !== localId));
    };

    const handleUpdatePendingDescription = (localId: string, desc: string) => {
        onChange(files.map(f => f.localId === localId ? { ...f, description: desc } : f))
    }

    return (
        <div className="space-y-3">
            <div
                className="border-2 border-dashed border-border/50 rounded-xl p-6 flex flex-col items-center gap-2 cursor-pointer hover:border-primary/40 hover:bg-primary/5 transition-all"
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files.length) handleFiles(e.dataTransfer.files); }}
            >
                <Paperclip className="w-6 h-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground text-center">
                    Arrastra archivos aquí o haz clic para seleccionar
                </p>
                <p className="text-xs text-muted-foreground/50">
                    Imágenes, PDF, Excel, Word — máx. 5 MB por archivo
                </p>
                <Button type="button" variant="outline" size="sm" className="cursor-pointer mt-1">
                    Seleccionar archivos
                </Button>
            </div>

            <input
                ref={inputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.xlsx,.xls,.docx,.doc"
                className="hidden"
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
            />

            {totalCount > 0 && (
                <div className="space-y-2">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <ImageIcon className="w-3.5 h-3.5" />
                        {totalCount} archivo{totalCount !== 1 ? "s" : ""}
                        {existingFileIds.length > 0 && files.length > 0 && (
                            <span className="text-muted-foreground/60">
                                — {existingFileIds.length} guardado{existingFileIds.length !== 1 ? "s" : ""}, {files.length} nuevo{files.length !== 1 ? "s" : ""}
                            </span>
                        )}
                    </p>
                    <div className="flex flex-wrap gap-3">
                        {existingFileIds.map((fileId, i) => (
                            <ExistingFileCard
                                key={fileId}
                                fileId={fileId}
                                description={existingDescriptions[i] ?? ""}
                                onRemove={() => onRemoveExisting?.(i)}
                                onDescriptionChange={(desc) => onUpdateExistingDescription?.(i, desc)}
                            />
                        ))}
                        {files.map((f) => (
                            <PendingFileCard
                                key={f.localId}
                                file={f}
                                onRemove={() => handleRemovePending(f.localId)}
                                onDescriptionChange={(desc) => handleUpdatePendingDescription(f.localId, desc)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
